import React, { useEffect, useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import { FaUser } from 'react-icons/fa';
import httpService from '../../../services/httpService';
import UserInfo from '../UserInfo';
import { TabSpinner, Th, EmptyRow, Avatar, formatRelative } from './shared';

const ListenersTab = ({ clubGuid }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedUserGuid, setSelectedUserGuid] = useState(null);

    useEffect(() => {
        httpService.get(`/dash/podcast-club/${clubGuid}/statistics/listeners`)
            .then((res) => setData(res.data))
            .catch((err) => setError(err.response?.data?.message || 'Error loading data'))
            .finally(() => setLoading(false));
    }, [clubGuid]);

    if (loading) return <TabSpinner />;
    if (error) return <Alert variant="danger">{error}</Alert>;
    if (!data) return null;

    const patterns = data.listener_patterns || [];
    const valuable = data.valuable_listeners || [];

    return (
        <div>
            {/* Listener practice patterns */}
            <div className="mb-5">
                <h6 className="fw-semibold mb-1">Listener practice patterns</h6>
                <p className="text-muted small mb-3">
                    Surfaces who is repeatedly working on what. Sorted by total practices — listeners at the top are the most consistently active in this club.
                </p>
                <Table hover responsive size="sm" className="align-middle">
                    <thead className="table-light">
                        <tr>
                            <Th tip="The subscriber's display name.">Listener</Th>
                            <Th tip="Each skill this listener tagged in their practices for this club, with the number of times they selected it. Multiple selections of the same skill indicate repeated focus.">Skills practised</Th>
                            <Th className="text-end" tip="Total number of practices this listener has recorded across all episodes in this club.">Times practised</Th>
                            <Th className="text-end" tip="The number of distinct episodes this listener has recorded at least one practice against. High breadth means the listener is engaging consistently across the whole club, not just one episode.">Episodes involved</Th>
                            <th style={{ width: 36 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {patterns.length === 0
                            ? <EmptyRow cols={5} />
                            : patterns.map((p, i) => (
                                <tr key={i}>
                                    <td>
                                        <div className="d-flex align-items-center gap-2">
                                            <Avatar guid={p.user_guid} />
                                            <span className="fw-medium">{p.listener_name}</span>
                                        </div>
                                    </td>
                                    <td>
                                        {p.skills?.length > 0
                                            ? p.skills.map((s, j) => (
                                                <span key={j} className="me-2 text-nowrap">
                                                    <Badge bg="light" text="dark" className="border me-1">{s.title}</Badge>
                                                    <span className="text-muted small">&times;{s.count}</span>
                                                </span>
                                            ))
                                            : <span className="text-muted">—</span>
                                        }
                                    </td>
                                    <td className="text-end">{p.total_practices}</td>
                                    <td className="text-end">{p.episodes_involved}</td>
                                    <td className="text-end">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="p-1 lh-1"
                                            onClick={() => setSelectedUserGuid(p.user_guid)}
                                            title="View listener profile"
                                        >
                                            <FaUser style={{ fontSize: 11 }} />
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        }
                    </tbody>
                </Table>
            </div>

            {/* Most valuable listeners */}
            <div>
                <h6 className="fw-semibold mb-1">Who are the most valuable listeners?</h6>
                <p className="text-muted small mb-3">
                    Sorted by episodes engaged — listeners who appear across more episodes have demonstrated broader, sustained interest in this club's content.
                </p>
                <Table hover responsive size="sm" className="align-middle">
                    <thead className="table-light">
                        <tr>
                            <Th tip="The subscriber's display name.">Listener</Th>
                            <Th className="text-end" tip="The number of distinct episodes this listener has recorded at least one practice for. A listener active across many episodes has engaged with the full arc of the club, not just a single topic.">Episodes engaged</Th>
                            <Th tip="Their most-practiced skill with its count, plus the total number of skill tags across all their practices in this club.">Skills practised</Th>
                            <Th tip="When this listener last recorded a practice in this club. Recency signals whether they are still actively learning or have gone quiet.">Last active</Th>
                            <Th className="text-end" tip="How broadly this listener engaged across the club's episodes. A listener active across all 6 episodes is fully engaged with the club's complete learning journey.">Engagement span</Th>
                            <th style={{ width: 36 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {valuable.length === 0
                            ? <EmptyRow cols={6} />
                            : valuable.map((l, i) => (
                                <tr key={i}>
                                    <td>
                                        <div className="d-flex align-items-center gap-2">
                                            <Avatar guid={l.user_guid} />
                                            <span className="fw-medium">{l.listener_name}</span>
                                        </div>
                                    </td>
                                    <td className="text-end">{l.episodes_engaged}</td>
                                    <td>
                                        {l.top_skill
                                            ? <>
                                                <Badge bg="light" text="dark" className="border me-1">{l.top_skill.title}</Badge>
                                                <span className="text-muted small">&times;{l.top_skill.count}</span>
                                                {l.skills_count > l.top_skill.count && (
                                                    <span className="text-muted small ms-1">&middot; {l.skills_count} total</span>
                                                )}
                                            </>
                                            : <span className="text-muted">—</span>
                                        }
                                    </td>
                                    <td className="text-muted">{formatRelative(l.last_interaction)}</td>
                                    <td className="text-end">{l.engagement_span} ep{l.engagement_span !== 1 ? 's' : ''}</td>
                                    <td className="text-end">
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            className="p-1 lh-1"
                                            onClick={() => setSelectedUserGuid(l.user_guid)}
                                            title="View listener profile"
                                        >
                                            <FaUser style={{ fontSize: 11 }} />
                                        </Button>
                                    </td>
                                </tr>
                            ))
                        }
                    </tbody>
                </Table>
            </div>

            {/* Listener profile modal */}
            <Modal show={!!selectedUserGuid} onHide={() => setSelectedUserGuid(null)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6 fw-semibold">Listener profile</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {selectedUserGuid && (
                        <UserInfo
                            key={selectedUserGuid}
                            userGuid={selectedUserGuid}
                            clubGuid={clubGuid}
                            context={{ type: 'club', data: {} }}
                        />
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default ListenersTab;
